'use client';

import { Ticket, UserCheck, HandHeart, ChevronRight } from 'lucide-react';
import type { RegistrationOption } from '@/types/serve-finder';

interface OptionChooserProps {
  options: RegistrationOption[];
  accentColor?: string;
  onServe?: () => void;
}

const iconMap = {
  ticket: Ticket,
  attend: UserCheck,
  serve: HandHeart,
} as const;

export function OptionChooser({ options, accentColor, onServe }: OptionChooserProps) {
  return (
    <div>
      <div className="space-y-2">
        {options.map((option, index) => {
          const Icon = iconMap[option.Icon];
          const isPrimary = index === 0 && !!accentColor;
          const isServeNav = option.Icon === 'serve' && onServe;

          const className = `group flex w-full items-center justify-between p-4 text-left transition-shadow hover:shadow-md ${accentColor && !isPrimary ? '' : !accentColor ? 'border-border bg-background border' : ''}`;
          const style = isPrimary
            ? { backgroundColor: accentColor }
            : accentColor
              ? {
                  border: '1px solid rgba(255,255,255,0.12)',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                }
              : undefined;

          const content = (
            <>
              <div className="flex items-center gap-3">
                <div className="flex w-6 shrink-0 items-center justify-center">
                  <Icon
                    className="h-5 w-5"
                    style={{
                      color: isPrimary ? '#ffffff' : (accentColor ?? 'var(--brand-secondary)'),
                    }}
                  />
                </div>
                <div>
                  <p
                    className="text-sm font-bold"
                    style={{ color: isPrimary || accentColor ? '#ffffff' : undefined }}
                  >
                    <span className={isPrimary || accentColor ? '' : 'text-foreground'}>
                      {option.Label}
                    </span>
                  </p>
                  <p
                    className="mt-0.5 text-xs"
                    style={{
                      color: isPrimary
                        ? 'rgba(255,255,255,0.8)'
                        : accentColor
                          ? 'rgba(255,255,255,0.6)'
                          : undefined,
                    }}
                  >
                    <span className={isPrimary || accentColor ? '' : 'text-muted-foreground'}>
                      {option.Description}
                    </span>
                  </p>
                </div>
              </div>
              <ChevronRight
                className="h-4 w-4 shrink-0 transition-colors"
                style={{
                  color: isPrimary
                    ? 'rgba(255,255,255,0.7)'
                    : accentColor
                      ? 'rgba(255,255,255,0.4)'
                      : undefined,
                }}
              />
            </>
          );

          if (isServeNav) {
            return (
              <button
                key={option.Label}
                type="button"
                onClick={onServe}
                className={className}
                style={style}
              >
                {content}
              </button>
            );
          }

          return (
            <a
              key={option.Label}
              href={option.URL}
              target="_blank"
              rel="noopener noreferrer"
              className={className}
              style={style}
            >
              {content}
            </a>
          );
        })}
      </div>
    </div>
  );
}
